import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  apaInTextAuthors,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  chicagoShortAuthors,
  naturalName,
  organizationName,
  type PartialDate,
  type PersonName,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import { readDate } from "@/lib/tool-kit/citation-sources-books/common";
import { titleCase } from "@/lib/tool-kit/citation-sources-more/common";
import { editionLabel } from "@/lib/tool-kit/citation-sources-more/lab-manual-citation-generator";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  mlaUrl,
  plain,
} from "@/lib/tool-kit/legal-sources/common";
import {
  beforeSubtitle,
  fullUrl,
  type MediaLabels,
  type MediaValues,
  mlaRoleNames,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";
import { publisherName } from "@/lib/tool-kit/special-sources/common";

/*
 * A board game, card game or other tabletop game, in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026). None of the three manuals prints a board-game
 * example, so this applies each style's own general pattern, as follows:
 * - APA 7: APA Style's advice for a source with no example is to answer the four
 *   questions of author, date, title and source, with a description in square brackets
 *   after the title for an unusual kind of work (the same device as [Video game] or
 *   [Poster presentation]). Library guides give a game that form —
 *   "Calder, T., & Vaughan, J. (2007). Sensory diet cards [Game]. Super Duper
 *   Publications." (Georgian College) — with the designer in the author position. The
 *   "(Designer)" role label follows APA's treatment of a film's director and is a
 *   convention. A game by a company such as Hasbro takes the company as the author, and
 *   then the publisher is not repeated.
 * - MLA 9: the MLA Style Center's video-game model — begin with the title (a game's
 *   creators are not authors), put the lead creator in the Contributor slot, "Directed
 *   by Sam Lake and Kyle Rowley, Remedy Entertainment / Epic Games, 2023. Sony
 *   PlayStation 5 game." — adapted by using "Designed by" and closing with the kind of
 *   game in place of the platform. In text the title stands in for an author.
 * - Chicago 18: no board-game example. The designer leads like an author, the kind of
 *   game is named after the title as Chicago names a medium (the TED sample's "Video,"),
 *   and the publisher and year follow in the usual form. A convention, not a published
 *   example.
 */

export const BOARD_GAME_FIELDS = {
  kind: "kind",
  designers: "designers",
  title: "title",
  edition: "edition",
  publisher: "publisher",
  year: "year",
  publisherAuthor: "publisherAuthor",
  url: "url",
  shortTitle: "shortTitle",
} as const;

const F = BOARD_GAME_FIELDS;

/** The kinds of tabletop game the tool describes. */
export type GameKind = "board" | "card" | "dice" | "tabletop" | "roleplaying";

const KINDS: readonly GameKind[] = [
  "board",
  "card",
  "dice",
  "tabletop",
  "roleplaying",
];

/** The kind of game as each style words it: the APA/Chicago form and the MLA one. */
const KIND_TEXT: Record<
  GameKind,
  { apa: string; chicago: string; mla: string }
> = {
  board: { apa: "Board game", chicago: "board game", mla: "Board game" },
  card: { apa: "Card game", chicago: "card game", mla: "Card game" },
  dice: { apa: "Dice game", chicago: "dice game", mla: "Dice game" },
  tabletop: {
    apa: "Tabletop game",
    chicago: "tabletop game",
    mla: "Tabletop game",
  },
  roleplaying: {
    apa: "Role-playing game",
    chicago: "role-playing game",
    mla: "Role-playing game",
  },
};

export interface BoardGameLabels extends MediaLabels {
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Game {
  kind: GameKind;
  designers: PersonName[];
  title: string;
  edition: string;
  publisher: string;
  year: PartialDate | null;
  publisherAuthor: boolean;
  url: string;
  shortTitle: string;
}

function readKind(text: string): GameKind {
  return KINDS.find((kind) => kind === text) ?? "board";
}

function readGame(values: MediaValues): Game {
  return {
    kind: readKind(get(values, F.kind)),
    designers: parsePeople(get(values, F.designers)),
    title: capitalizeSubtitle(get(values, F.title)),
    edition: editionLabel(get(values, F.edition)),
    publisher: get(values, F.publisher),
    year: readDate(get(values, F.year)),
    publisherAuthor: get(values, F.publisherAuthor) === "true",
    url: fullUrl(get(values, F.url)),
    shortTitle: get(values, F.shortTitle),
  };
}

function yearText(game: Game): string {
  return game.year ? String(game.year.year) : "n.d.";
}

/* ---------------------------------------------------------------------- APA */

/** The APA author: the designers, or the publisher as a group when asked or alone. */
function apaNames(game: Game): { names: PersonName[]; isPublisher: boolean } {
  const usePublisher =
    game.publisher !== "" &&
    (game.publisherAuthor || game.designers.length === 0);
  if (usePublisher) {
    return {
      names: [organizationName(publisherName(game.publisher, "apa"))],
      isPublisher: true,
    };
  }
  return { names: game.designers, isPublisher: false };
}

function apaReference(game: Game): CitationParts {
  const { names, isPublisher } = apaNames(game);
  const year = yearText(game);
  const authors = apaAuthors(names);
  const role =
    authors && !isPublisher
      ? ` (${names.length > 1 ? "Designers" : "Designer"})`
      : "";
  const lead = authors
    ? `${authors}${role}${role ? "." : closer(authors)}`
    : "";
  const edition = game.edition ? ` (${game.edition})` : "";
  const publisher =
    game.publisher && !isPublisher
      ? ` ${withPeriod(publisherName(game.publisher, "apa"))}`
      : "";
  const url = game.url ? ` ${game.url}` : "";
  const kind = ` [${KIND_TEXT[game.kind].apa}].`;
  if (lead === "") {
    return tidyParts([
      italic(game.title),
      plain(`${edition}${kind} (${year}).${publisher}${url}`),
    ]);
  }
  return tidyParts([
    plain(`${lead} (${year}). `),
    italic(game.title),
    plain(`${edition}${kind}${publisher}${url}`),
  ]);
}

function apaCitation(game: Game, narrative: boolean): CitationParts {
  const { names } = apaNames(game);
  const year = yearText(game);
  if (names.length === 0) {
    const short = beforeSubtitle(game.title);
    return narrative
      ? [italic(short), plain(` (${year})`)]
      : [plain("("), italic(short), plain(`, ${year})`)];
  }
  const who = apaInTextAuthors(names, narrative);
  return [plain(narrative ? `${who} (${year})` : `(${who}, ${year})`)];
}

function apaOutput(game: Game, labels: BoardGameLabels): CitationOutput {
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaReference(game) },
      { label: labels.parenthetical, parts: apaCitation(game, false) },
      { label: labels.narrative, parts: apaCitation(game, true) },
    ],
    note: labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

function mlaWorksCited(game: Game): CitationParts {
  const title = titleCase(game.title);
  const designers = game.designers.length
    ? `Designed by ${mlaRoleNames(game.designers.map(naturalName))}`
    : "";
  const publisher = game.publisher ? publisherName(game.publisher, "mla") : "";
  const link = game.url ? mlaUrl(game.url) : "";
  const tail = [
    designers,
    game.edition,
    publisher,
    game.year ? String(game.year.year) : "",
    link,
  ]
    .filter(Boolean)
    .join(", ");
  return tidyParts([
    italic(title),
    plain(
      `${closer(title)}${tail ? ` ${withPeriod(tail)}` : ""} ${KIND_TEXT[game.kind].mla}.`
    ),
  ]);
}

function mlaOutput(game: Game, labels: BoardGameLabels): CitationOutput {
  const short = titleCase(beforeSubtitle(game.title));
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: mlaWorksCited(game) },
      {
        label: labels.inText,
        parts: [plain("("), italic(short), plain(")")],
      },
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

function chicagoImprint(game: Game): string {
  const publisher = game.publisher
    ? publisherName(game.publisher, "chicago")
    : "";
  return [publisher, yearText(game)].filter(Boolean).join(", ");
}

function chicagoNote(game: Game, title: string): CitationParts {
  const names = chicagoNoteAuthors(game.designers);
  const lead = names ? `${names}, ` : "";
  const edition = game.edition ? `, ${game.edition}` : "";
  const link = game.url ? `, ${game.url}` : "";
  return tidyParts([
    plain(lead),
    italic(title),
    plain(
      `${edition}, ${KIND_TEXT[game.kind].chicago} (${chicagoImprint(game)})${link}.`
    ),
  ]);
}

function chicagoShortNote(game: Game, title: string): CitationParts {
  const short =
    game.shortTitle || titleCase(beforeSubtitle(game.title)) || title;
  const names = chicagoShortAuthors(game.designers);
  return tidyParts([
    plain(names ? `${names}, ` : ""),
    italic(short),
    plain("."),
  ]);
}

function chicagoBibliography(game: Game, title: string): CitationParts {
  const names = chicagoBibliographyAuthors(game.designers);
  const lead = names ? `${names}${closer(names)} ` : "";
  const edition = game.edition ? ` ${game.edition}` : "";
  const kind = capitalizeFirst(KIND_TEXT[game.kind].chicago);
  const link = game.url ? ` ${game.url}.` : "";
  return tidyParts([
    plain(lead),
    italic(title),
    plain(
      `${closer(title)}${edition} ${kind}. ${withPeriod(chicagoImprint(game))}${link}`
    ),
  ]);
}

function capitalizeFirst(text: string): string {
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

function chicagoOutput(game: Game, labels: BoardGameLabels): CitationOutput {
  const title = titleCase(game.title);
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: chicagoNote(game, title) },
      { label: labels.shortNote, parts: chicagoShortNote(game, title) },
      { label: labels.bibliography, parts: chicagoBibliography(game, title) },
    ],
    note: labels.chicagoNote,
  };
}

export function boardGameIsReady(values: MediaValues): boolean {
  return get(values, F.title) !== "";
}

export function formatBoardGame(
  values: MediaValues,
  labels: BoardGameLabels
): CitationOutput[] {
  const game = readGame(values);
  return [
    apaOutput(game, labels),
    mlaOutput(game, labels),
    chicagoOutput(game, labels),
  ];
}
